import { Auth, UserCredential } from 'firebase/auth';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { Router } from '../app/router';
import { AuthStore } from '../modules/auth/auth.store';
import { showToast } from '../shared/components/Toast';
const SETTINGS_ROUTE = '/settings';

/* ------------------------------------------------------------------ */
// Configuration
/* ------------------------------------------------------------------ */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/email-already-in-use': 'An account with that email already exists. Sign in instead.',
  'auth/weak-password': 'Please choose a stronger password (at least 8 characters).',
  'auth/invalid-credential': 'Incorrect email or password. Please try again.',
  'auth/user-not-found': 'No account found with that email. Create one instead.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/account-exists-with-different-credential':
    'That email is already registered with Google. Please continue with Google instead.',
  'auth/internal-error': 'Google Sign-In encountered a domain/connection issue. Ensure Google Auth is enabled & domain authorized in Firebase Console.',
  'auth/operation-not-allowed': 'Google Sign-In is not enabled in your Firebase Project Console (Authentication > Sign-in method).',
  'auth/unauthorized-domain': 'This domain (localhost or your site URL) is not authorized in Firebase Console (Authentication > Settings > Authorized Domains).',
  'auth/popup-closed-by-user': '', // ignore
  'auth/cancelled-popup-request': '', // ignore
};

/* ------------------------------------------------------------------ */
// Types
/* ------------------------------------------------------------------ */

export interface AuthPageDependencies {
  auth: Auth;
  authStore: AuthStore;
  router: Router;
}

interface AuthPageProps {
  container: HTMLElement;
  deps: AuthPageDependencies;
}

/* ------------------------------------------------------------------ */
// Render
/* ------------------------------------------------------------------ */

function renderAuthPageHtml(mode: 'signin' | 'signup'): string {
  const isSignup = mode === 'signup';
  const title = isSignup ? 'Create your account' : 'Welcome back';
  const subtitle = isSignup
    ? 'Join Zuri and start connecting with verified singles.'
    : 'Sign in to continue your journey.';
  const submitLabel = isSignup ? 'Create Account' : 'Sign In';
  const toggleLabel = isSignup
    ? 'Already have an account? Sign in instead'
    : 'New to Zuri? Create an account';

  return `
    <main class="section pt-nav">
      <div class="container max-w-md">
        <div class="card card-body">
          <h2 class="text-center mb-2">${title}</h2>
          <p class="text-sm text-center mb-6 text-muted">${subtitle}</p>

          <button class="btn btn-secondary w-full mb-4" id="googleSignInBtn">
            <i class="fa-brands fa-google"></i> Continue with Google
          </button>

          <div class="divider text-xs text-center mb-4 text-muted">or use email</div>

          <form id="authForm" novalidate>
            <div class="form-group mb-4" id="nameGroup" ${isSignup ? '' : 'hidden'}>
              <label class="text-sm mb-1" for="authName">Full name</label>
              <input class="input w-full" type="text" id="authName" autocomplete="name" />
            </div>

            <div class="form-group mb-4">
              <label class="text-sm mb-1" for="authEmail">Email address</label>
              <input class="input w-full" type="email" id="authEmail" autocomplete="email" required />
            </div>

            <div class="form-group mb-4">
              <label class="text-sm mb-1" for="authPassword">Password</label>
              <input class="input w-full" type="password" id="authPassword" autocomplete="current-password" required />
            </div>

            <div class="form-group mb-4" id="ageGroup" ${isSignup ? '' : 'hidden'}>
              <label class="flex items-center gap-2 text-sm">
                <input type="checkbox" id="ageConfirm" />
                I confirm I am 18 years of age or older
              </label>
            </div>

            <button type="submit" class="btn btn-primary w-full" id="authSubmitBtn">${submitLabel}</button>
          </form>

          <button class="btn btn-ghost w-full mt-4" id="authToggleBtn">${toggleLabel}</button>

          <p class="text-xs text-center mt-6 text-muted">
            Your data is protected by Zuri's privacy policy.
          </p>
        </div>
      </div>
    </main>
  `;
}

/* ------------------------------------------------------------------ */
// Main
/* ------------------------------------------------------------------ */

export function renderAuthPage({ container, deps }: AuthPageProps): () => void {
  const { auth, authStore, router } = deps;

  // If already authenticated, redirect immediately (no render needed)
  if (authStore.isAuthenticated) {
    router.navigate(SETTINGS_ROUTE);
    return () => {}; // no-op cleanup
  }

  let currentMode: 'signin' | 'signup' = 'signin';

  // Render initial HTML
  container.innerHTML = renderAuthPageHtml(currentMode);

  // Cache DOM elements
  const elements = {
    nameGroup: document.getElementById('nameGroup')!,
    ageGroup: document.getElementById('ageGroup')!,
    ageConfirm: document.getElementById('ageConfirm') as HTMLInputElement,
    submitBtn: document.getElementById('authSubmitBtn') as HTMLButtonElement,
    toggleBtn: document.getElementById('authToggleBtn') as HTMLButtonElement,
    googleBtn: document.getElementById('googleSignInBtn') as HTMLButtonElement,
    form: document.getElementById('authForm') as HTMLFormElement,
    emailInput: document.getElementById('authEmail') as HTMLInputElement,
    passwordInput: document.getElementById('authPassword') as HTMLInputElement,
    nameInput: document.getElementById('authName') as HTMLInputElement,
  };

  // Helper: update UI for the current mode
  function updateUIMode(mode: 'signin' | 'signup'): void {
    currentMode = mode;
    const isSignup = mode === 'signup';
    elements.nameGroup.hidden = !isSignup;
    elements.ageGroup.hidden = !isSignup;
    elements.submitBtn.textContent = isSignup ? 'Create Account' : 'Sign In';
    elements.toggleBtn.innerHTML = isSignup
      ? 'Already have an account? Sign in instead'
      : 'New to Zuri? Create an account';
  }

  // ----- Helper: safe navigation after auth -----
  function redirectToSettings() {
    setTimeout(() => router.navigate(SETTINGS_ROUTE), 500);
  }

  // ----- Helper: show user-friendly error -----
  function handleAuthError(err: unknown): void {
    const error = err as { code?: string; message?: string };
    const code = error.code || '';
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      return; // ignore
    }
    const message = AUTH_ERROR_MESSAGES[code] || error.message || 'Authentication failed. Please try again.';
    showToast(message, 'error');
  }

  // Check for redirect result on page load (in case popup fell back to redirect)
  getRedirectResult(auth)
    .then((result) => {
      if (result?.user) {
        showToast('Welcome to Zuri!', 'success');
        redirectToSettings();
      }
    })
    .catch((err) => {
      handleAuthError(err);
    });

  // ----- Google sign-in -----
  async function handleGoogleSignIn(): Promise<void> {
    // Age check for new signups
    if (currentMode === 'signup' && !elements.ageConfirm.checked) {
      showToast('Please confirm you are 18 or older before continuing with Google.', 'error');
      return;
    }

    const btn = elements.googleBtn;
    btn.disabled = true;
    btn.textContent = 'Connecting to Google…';

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      showToast('Welcome to Zuri!', 'success');
      redirectToSettings();
    } catch (err) {
      console.error('Google Sign-In error (full object):', err);
      const error = err as { code?: string; customData?: { email?: string } };
      const code = error.code || '';

      if (code === 'auth/internal-error' || code === 'auth/popup-blocked') {
        try {
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          showToast('Redirecting to Google Sign-In...', 'info');
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectErr) {
          handleAuthError(redirectErr);
        }
      } else if (code === 'auth/account-exists-with-different-credential') {
        const email = error.customData?.email;
        try {
          if (email) {
            const methods = await fetchSignInMethodsForEmail(auth, email);
            if (methods.includes('password')) {
              showToast(
                'That email is already registered with a password. Please sign in with email instead.',
                'error',
              );
            } else {
              showToast('An account already exists with a different sign-in method.', 'error');
            }
          } else {
            showToast('An account already exists with a different sign-in method.', 'error');
          }
        } catch {
          showToast('An account already exists with a different sign-in method.', 'error');
        }
      } else {
        handleAuthError(err);
      }
    } finally {
      btn.disabled = false;
      btn.innerHTML = '<i class="fa-brands fa-google"></i> Continue with Google';
    }
  }

  // ----- Email/password submission -----
  async function handleEmailSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const email = elements.emailInput.value.trim();
    const password = elements.passwordInput.value.trim();
    const name = elements.nameInput.value.trim();

    // Client-side validations
    if (!email) {
      showToast('Please enter your email address.', 'error');
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }
    if (!password || password.length < 8) {
      showToast('Password must be at least 8 characters.', 'error');
      return;
    }
    if (currentMode === 'signup' && !elements.ageConfirm.checked) {
      showToast('You must confirm you are 18 or older to continue.', 'error');
      return;
    }

    const btn = elements.submitBtn;
    btn.disabled = true;
    btn.textContent = 'Please wait…';

    try {
      let userCredential: UserCredential;
      if (currentMode === 'signup') {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const displayName = name || email.split('@')[0];
        await updateProfile(userCredential.user, { displayName });
        showToast('Account created — welcome to Zuri!', 'success');
      } else {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
        showToast('Signed in successfully.', 'success');
      }
      redirectToSettings();
    } catch (err) {
      handleAuthError(err);
    } finally {
      btn.disabled = false;
      btn.textContent = currentMode === 'signup' ? 'Create Account' : 'Sign In';
    }
  }

  // ----- Toggle mode -----
  function toggleMode(): void {
    updateUIMode(currentMode === 'signin' ? 'signup' : 'signin');
  }

  // ----- Set up event listeners using AbortController for cleanup -----
  const abortController = new AbortController();
  const { signal } = abortController;

  // Use event delegation on the container for buttons and form submission
  container.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('button');
      if (!btn) return;

      if (btn.id === 'googleSignInBtn') {
        e.preventDefault();
        void handleGoogleSignIn();
      } else if (btn.id === 'authToggleBtn') {
        e.preventDefault();
        toggleMode();
      }
    },
    { signal },
  );

  // Form submit
  elements.form.addEventListener('submit', (e) => void handleEmailSubmit(e), { signal });

  // ----- Cleanup -----
  return function unmount() {
    abortController.abort(); // removes all listeners added with this signal
    container.innerHTML = ''; // clear DOM
  };
}
